/**
 * EventDetailsPopover - Detail panel that renders when a user clicks
 * an event chip on the calendar or a pin on the map.
 *
 * Surfaces:
 *   - Title + record-type badge
 *   - Time, status, company, location, assignees
 *   - "Open record" link into the WO/Inspection detail page
 *   - "Add to Calendar" CTA — generates a .ics file via icsExport util
 *   - "Directions" CTA — opens platform maps with destination filled in
 *     (deep-link helper handles iOS vs everyone-else routing)
 *
 * Renders as a self-contained card. Parents can wrap it in a Modal
 * (for the calendar's click-to-open flow) or place it inline (the
 * Map's Leaflet popup). The component is render-only — closing /
 * dismissing is the parent's responsibility.
 */
import React from 'react';
import { EventTypeBadge } from '../../atoms';
import { getEventCategory, type ScheduledEvent } from '../../../services/scheduledEventsService';
import { downloadEventAsIcs } from '../../../utils/icsExport';
import { getDirectionsUrl } from '../../../utils/directionsLink';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface EventDetailsPopoverProps {
    event: ScheduledEvent;
    /** Optional close handler — when supplied, an "×" button renders in the header. */
    onClose?: () => void;
    /** Optional handler when the user clicks "Open record".
     *  Defaults to a full-page navigation via window.location. */
    onOpenRecord?: (event: ScheduledEvent) => void;
    /** Optional extra classes for the outer wrapper */
    className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** Format an ISO datetime as a short local "Mon Jan 1, 9:30 AM" string. */
function formatStart(event: ScheduledEvent): string {
    if (event.allDay) {
        // All-day records carry a bare "YYYY-MM-DD" start (server sets
        // start = scheduled_date when start_time is null). new Date("YYYY-MM-DD")
        // parses as UTC midnight, which renders as the PREVIOUS day in
        // negative-UTC-offset timezones (all of the Americas). Parse at local
        // midnight instead so the displayed day matches the scheduled day.
        const dateOnly = String(event.start).slice(0, 10);
        const allDayDate = new Date(`${dateOnly}T00:00:00`);
        if (Number.isNaN(allDayDate.getTime())) return event.scheduledDate;
        return allDayDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    }
    const date = new Date(event.start);
    if (Number.isNaN(date.getTime())) return event.scheduledDate;
    return date.toLocaleString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

/** Build a single-line address for display. */
function formatFullAddress(event: ScheduledEvent): string | null {
    const parts = [
        event.address,
        event.addressLine2,
        event.city && event.state ? `${event.city}, ${event.state}` : event.city || event.state,
        event.zip,
    ].filter(Boolean) as string[];
    if (parts.length === 0) return null;
    return parts.join(' • ');
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const EventDetailsPopover: React.FC<EventDetailsPopoverProps> = ({
    event,
    onClose,
    onOpenRecord,
    className,
}) => {
    /* Resolve the directions URL once — null when the event has no
     * usable address (button is hidden in that case). */
    const directionsUrl = getDirectionsUrl(event);

    /** Click handler for "Open record" — defers to the parent if
     *  provided, otherwise full-page navigates. */
    const handleOpenRecord = () => {
        if (onOpenRecord) {
            onOpenRecord(event);
        } else if (typeof window !== 'undefined') {
            window.location.href = event.detailUrl;
        }
    };

    return (
        /* Content-only — no bg/border/shadow/fixed width here.
         * Every caller already provides outer chrome (Modal on Calendar
         * + Map pages; Leaflet's <Popup> on the Map's pin click), so
         * adding our own produced a visible box-in-box. The parent's
         * chrome is the single source of truth for popover styling. */
        <div
            className={['event-details-popover', 'w-full', className ?? ''].join(' ')}
            data-ref="organism-event-details-popover"
            role="dialog"
            aria-label="Event details"
        >
            {/* Header — type badge + optional close button.
             * The close button is suppressed when the parent (Modal /
             * Leaflet Popup) provides its own dismiss affordance, so
             * we never end up with two X buttons stacked. */}
            <div className="event-details-popover__header flex items-start justify-between gap-8px">
                <EventTypeBadge type={getEventCategory(event)} size="sm" />
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="event-details-popover__close inline-flex items-center justify-center w-44px h-44px -mr-8px -mt-8px shrink-0 rounded-full text-brand-text-muted hover:text-brand-text hover:bg-brand-surface transition-colors text-16px leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                )}
            </div>

            {/* Title + start datetime
             * Side padding removed — the host (Modal body or Leaflet
             * Popup) supplies the outer padding now that the popover is
             * chrome-less. */}
            <div className="pt-8px pb-4px">
                <div className="text-15px font-sans font-semibold text-brand-text">{event.title}</div>
                <div className="text-12px text-brand-text-muted mt-2px">{formatStart(event)}</div>
            </div>

            {/* Field rows */}
            <dl className="event-details-popover__fields py-8px space-y-6px text-13px">
                {event.companyName && (
                    <div className="flex items-baseline gap-8px">
                        <dt className="text-11px uppercase tracking-wide text-brand-text-muted w-[60px] flex-shrink-0">Company</dt>
                        <dd className="text-brand-text">{event.companyName}</dd>
                    </div>
                )}
                {event.locationName && (
                    <div className="flex items-baseline gap-8px">
                        <dt className="text-11px uppercase tracking-wide text-brand-text-muted w-[60px] flex-shrink-0">Location</dt>
                        <dd className="text-brand-text">{event.locationName}</dd>
                    </div>
                )}
                {formatFullAddress(event) && (
                    <div className="flex items-baseline gap-8px">
                        <dt className="text-11px uppercase tracking-wide text-brand-text-muted w-[60px] flex-shrink-0">Address</dt>
                        <dd className="text-brand-text">{formatFullAddress(event)}</dd>
                    </div>
                )}
                {event.assignees.length > 0 && (
                    <div className="flex items-baseline gap-8px">
                        <dt className="text-11px uppercase tracking-wide text-brand-text-muted w-[60px] flex-shrink-0">Assigned</dt>
                        <dd className="text-brand-text">{event.assignees.map((a) => a.name).join(', ')}</dd>
                    </div>
                )}
                {event.status && (
                    <div className="flex items-baseline gap-8px">
                        <dt className="text-11px uppercase tracking-wide text-brand-text-muted w-[60px] flex-shrink-0">Status</dt>
                        <dd className="text-brand-text">{event.status}</dd>
                    </div>
                )}
            </dl>

            {/* Action row — Add to Calendar + Directions + Open record.
             * Side padding removed for the same reason as above; vertical
             * spacing kept so the top border separates the row visually. */}
            <div className="event-details-popover__actions flex flex-wrap items-center gap-6px pb-4px pt-12px mt-4px border-t border-brand-border">
                <button
                    type="button"
                    onClick={() => downloadEventAsIcs(event)}
                    className="inline-flex items-center gap-4px px-10px py-8px min-h-44px desktop:min-h-36px text-12px font-sans font-medium text-brand-text border border-brand-border rounded-md hover:bg-brand-surface"
                >
                    {/* Calendar plus icon */}
                    <svg className="w-12px h-12px" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                    </svg>
                    Add to Calendar
                </button>

                {directionsUrl && (
                    <a
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-4px px-10px py-8px min-h-44px desktop:min-h-36px text-12px font-sans font-medium text-brand-text border border-brand-border rounded-md hover:bg-brand-surface"
                    >
                        {/* Map pin icon */}
                        <svg className="w-12px h-12px" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                        </svg>
                        Directions
                    </a>
                )}

                <button
                    type="button"
                    onClick={handleOpenRecord}
                    className="inline-flex items-center gap-4px px-10px py-8px min-h-44px desktop:min-h-36px text-12px font-sans font-medium text-white bg-brand-primary rounded-md hover:bg-brand-primary-hover ml-auto"
                >
                    Open record
                    <svg className="w-12px h-12px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default EventDetailsPopover;
